import { useEffectAsync } from '../../../helpers';
import {
	EventType,
	Meta,
	useLazyWorkplaceEventsQuery,
} from '../../../graphql/graphql';
import { Moment } from 'moment';
import { useAppContext } from '../../../Services/AppContext';
import useTranslation from '../../../Hooks/useTranslation';
import { getMomentDate } from '../../../Utils/dates.util';

interface Worker {
	name: string;
	id: string;
	role: { role: { name: string } }[];
}

interface CalendarWorkplaceEvent {
	id: string;
	title?: string;
	doctor: Worker;
	workplace?: string;
	workplaceId: string;
	start: string;
	end: string;
	backgroundColor: string;
	borderColor: string;
	textColor: string;
	resourceId: string;
	note?: string;
	meta?: Meta[];
	workers?: Worker[];
	shift: {
		type: string;
		label: string;
	};
	createdBy: {
		name?: string;
	};
	allDay?: boolean;
}

export default function useWorkplaceEvents() {
	const { dateFormatMoment } = useAppContext();
	const { t } = useTranslation();
	const [getWorkplaceEvents] = useLazyWorkplaceEventsQuery();

	useEffectAsync(async () => {}, []);

	const getWorkersAbsenceEvents = async ({
		dateFrom,
		dateTo,
		allDay,
		workplaceIds,
	}: {
		dateFrom: Moment;
		dateTo: Moment;
		allDay?: boolean;
		workplaceIds?: string[];
	}) => {
		const data = await getWorkplaceEvents({
			start: dateFrom.format(),
			end: dateTo.format(),
			types: [EventType.Absence],
			workplaceIds,
		});
		const tempEvents: CalendarWorkplaceEvent[] = [];

		data?.data?.workplaceEvents?.data?.forEach(event => {
			let workers: {
				name: string;
				id: string;
				role: { role: { name: string } }[];
			}[] = [];

			event?.eventMembers?.forEach((user, index) => {
				const userData = user?.user;

				if (userData?.isDoctor) {
					if (userData?.profile?.fullName && userData?.id) {
						workers.push({
							role: [{ role: { name: t('base.shared.doctor') ?? '' } }],
							name: userData?.profile?.fullName,
							id: userData?.id,
						});
					}
				}
			});

			const dateFrom = getMomentDate(event?.dateFrom);
			let dateTo = getMomentDate(event?.dateTo);

			let label = `${t('base.shared.absence')}: ${dateFrom.format(
				dateFormatMoment,
			)} - ${dateTo.format(dateFormatMoment)}`;

			if (dateFrom.format('yyyy-MM-DD') === dateTo.format('yyyy-MM-DD')) {
				label = `${t('base.shared.absence')}: ${dateFrom.format(
					'HH:mm',
				)} - ${dateTo.format('HH:mm')}`;
			}

			if (allDay) {
				dateTo = dateTo.add(1, 'day').startOf('day');
			}

			tempEvents.push({
				id: event?.id!,
				title: event?.title ?? undefined,
				doctor: workers[0],
				workplace: event?.calendar?.workplace?.doctorDisplayName ?? undefined,
				workplaceId: event?.calendar?.workplaceId,
				start: dateFrom.format(),
				end: dateTo.format(),
				backgroundColor: event?.layout.backgroundColor,
				borderColor: event?.layout.backgroundColor,
				textColor: event?.layout.fontColor,
				resourceId: event?.calendar?.workplaceId,
				note: event?.description ?? undefined,
				meta: event?.meta as Meta[],
				workers: allDay ? workers : undefined,
				shift: {
					type: 'Absence',
					label: label,
				},
				createdBy: {
					name: event?.createdBy?.profile?.fullName ?? undefined,
				},
				allDay,
			});
		});

		return tempEvents;
	};

	return { getWorkersAbsenceEvents };
}
